import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { FileService } from './file.service';
import { ModalService } from './modal.service';
import { BodyImage } from '../models/app-release';
import { bodyImageFileIds, renderMarkdown, resolveBodyImages, splitSlides } from '../markdown';

/**
 * Turns a Markdown notification body into HTML the inbox and the popup can
 * show, with its `conductor-file:<id>` markers resolved to signed URLs
 * (image → <img>, mp4 → <video>).
 *
 * Resolution is client-side: the same body reaches the client three ways
 * (inbox fetch, live AppSync push, composer preview) and only one of those is
 * a server read, so resolving here keeps a single code path. Links are cached
 * per file for the session, well inside their six-hour life.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationBodyService {
  private static readonly LINK_TTL_MS = 5 * 60 * 60 * 1000;

  private links = new Map<number, { at: number; url$: Observable<string | null> }>();

  constructor(
    private fileService: FileService,
    private modalService: ModalService,
    private sanitizer: DomSanitizer
  ) {}

  /**
   * The popup for a notification body, the same everywhere it can open (live
   * push, the inbox "View" button, the composer's Preview):
   *   Markdown with 2+ `---` slides, or sent full screen → the slideshow modal.
   *   Anything else                                     → the plain "Notification" dialog.
   */
  showPopup(
    body: string,
    options: { format?: 'md' | null; display?: 'fullscreen' | null; title?: string } = {}
  ): void {
    const title = options.title || 'Notification';
    const md = body || '';
    const fullscreen = options.display === 'fullscreen';

    if (options.format === 'md' && (fullscreen || splitSlides(md).length > 1)) {
      this.resolveMedia(md).subscribe((bodyImages) => {
        this.modalService.releaseNotes({
          release: { title, body: md, bodyImages },
          updatePending: false,
          fullscreen,
        });
      });
      return;
    }

    const html$ = options.format === 'md' ? this.renderToHtml(md) : of(md);

    html$.subscribe((html) => {
      this.modalService.generic({
        title,
        copy: [html],
        buttons: [{ label: 'Ok', key: 'close', class: '' }],
      });
    });
  }

  /** Markdown → HTML string, media resolved. For the generic popup (which takes strings). */
  renderToHtml(md: string): Observable<string> {
    return this.resolveMedia(md).pipe(map((media) => renderMarkdown(resolveBodyImages(md, media))));
  }

  /** Markdown → trusted HTML, media resolved. For [innerHTML]. */
  render(md: string): Observable<SafeHtml> {
    return this.renderToHtml(md).pipe(map((html) => this.sanitizer.bypassSecurityTrustHtml(html)));
  }

  /** Every marker in `md` that could be resolved; missing files are dropped, never broken. */
  resolveMedia(md: string): Observable<BodyImage[]> {
    const ids = bodyImageFileIds(md);

    if (!ids.length) return of([]);

    return forkJoin(
      ids.map((fileId) => this.link(fileId).pipe(map((url) => (url ? ({ fileId, url } as BodyImage) : null))))
    ).pipe(map((list) => list.filter((i): i is BodyImage => !!i)));
  }

  private link(fileId: number): Observable<string | null> {
    const now = Date.now();
    const cached = this.links.get(fileId);

    if (cached && now - cached.at < NotificationBodyService.LINK_TTL_MS) {
      return cached.url$;
    }

    const url$ = this.fileService.getPreviewLink(String(fileId)).pipe(
      catchError(() => of(null)),
      shareReplay(1)
    );

    this.links.set(fileId, { at: now, url$ });

    return url$;
  }
}
